import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import type { ITableAllocation } from "@/data/model/Table";
import { exceptionTypes } from "@/data/util/Config";

import { logError } from "@/shared/logging/errors";

import { TastierClient } from "@/app/TastierClient";
import { trans } from "@/app/localization/Trans";
import { TableInfoPopover } from "@/app/popover/TableInfoPopover";
import { TableSelectionPopover } from "@/app/popover/TableSelectionPopover";
import { Popup } from "@/app/ui/Popup";

export function handleAllocationError(config: { e: DaoError; resetOnFailure: boolean; onReset?: () => void }) {
    const e = config.e;

    if (isAdditionalInfoRequired(e)) {
        showAdditionalInfoPopover(e);
    } else if (isTableNotAvailable(e)) {
        showTableNotAvailableMessage(e);

        if (config.resetOnFailure && config.onReset) {
            config.onReset();
        }
    } else if (isUnauthorized(e)) {
        showUnauthorizedMessageAndSignOut();
    } else {
        if (config.resetOnFailure && config.onReset) {
            config.onReset();
        }

        throw new Error("Failed to allocate to table.");
    }
}

export function isAdditionalInfoRequired(e: DaoError) {
    return (
        e.type === exceptionTypes.needTableForAllocationException ||
        e.type === exceptionTypes.needAdditionalInfoForAllocationException
    );
}

export function showAdditionalInfoPopover(e: DaoError) {
    if (e.additionalData.customTranslations !== undefined) trans.temporaryChange(e.additionalData.customTranslations);

    if (e.type === exceptionTypes.needTableForAllocationException)
        new TableSelectionPopover(e.additionalData.locationId, e.additionalData.locationName).show();
    else if (e.type === exceptionTypes.needAdditionalInfoForAllocationException)
        new TableInfoPopover(
            e.additionalData.locationId,
            e.additionalData.locationName,
            e.additionalData.tableNumber
        ).show();
}

export function isTableNotAvailable(e: DaoError) {
    return (
        e.type === exceptionTypes.tableNumberDoesNotExistException ||
        e.type === exceptionTypes.locationSuspendedException ||
        e.type === exceptionTypes.tableIsDeactivatedException ||
        e.type === exceptionTypes.roomIsDeactivatedException
    );
}

export function showTableNotAvailableMessage(e: DaoError) {
    let msg = trans.allocation.allocationFailed;
    switch (e.type) {
        case exceptionTypes.tableNumberDoesNotExistException:
            msg = trans.allocation.tableDoesNotExist;
            break;
        case exceptionTypes.tableIsDeactivatedException:
            msg = trans.allocation.tableDoesNotExist;
            break;
        case exceptionTypes.roomIsDeactivatedException:
            msg = trans.allocation.tableDoesNotExist;
            break;
        case exceptionTypes.locationSuspendedException:
            msg = trans.allocation.locationSuspended;
            break;
    }

    Popup.showWithOptions({ message: msg });
}

export function isUnauthorized(e: DaoError) {
    return e.type === exceptionTypes.unauthorizedException;
}

export function showUnauthorizedMessageAndSignOut() {
    Popup.show(trans.messages.unauthorizedPleaseRefresh, async () => {
        try {
            await TastierClient.identityContext.signOut();
        } catch (ex) {
            logError(ex);
        } finally {
            location.href = "/";
        }
    });
}

export function hasAllocationChanged(
    previousAllocation: ITableAllocation | undefined,
    newAllocation: ITableAllocation | undefined
) {
    if (previousAllocation !== undefined && newAllocation === undefined) {
        return true;
    }

    if (previousAllocation === undefined && newAllocation !== undefined) return true;

    if (previousAllocation !== undefined && newAllocation !== undefined) {
        if (
            previousAllocation.tableId !== newAllocation.tableId ||
            previousAllocation.locationId !== newAllocation.locationId ||
            previousAllocation.guestNumber !== newAllocation.guestNumber
        )
            return true;
    }

    return false;
}
